import { UseFormReturn } from '@graphcommerce/ecommerce-ui'
import { filterNonNullableKeys } from '@graphcommerce/next-ui'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { OptionTypeRenderer } from '../../Product/ProductCustomizable/ProductCustomizable'
import { FourOverCustomOptionModifier } from '@graphcommerce/graphql-mesh'

export const ColorspecDropDownOption = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableDropDownOption']> & {
    form: UseFormReturn<any>
    selectedVariantSku?: string | null
    quantities: Record<number, number>
    fouroverOptionModifier?: FourOverCustomOptionModifier
  }
>((props) => {
  const {
    uid,
    required,
    optionIndex,
    index,
    title,
    dropdownValue,
    form,
    quantities,
    fouroverOptionModifier,
  } = props
  const { register, setValue, getFieldState } = form

  const [color, setColor] = useState<null | string>()
  const { watch } = form

  const quantity = watch('quantity')

  const enteredOptions = watch('enteredOptions')

  const uniqueColorOptions = useMemo(() => {
    const uniqueTitles: string[] = []
    if (!dropdownValue) {
      return []
    }
    return dropdownValue
      .filter((v) => v && v.price)
      .map((v) => {
        const valueParts = (v?.title || '').split(' - ')
        if (valueParts.length > 1) {
          return {
            ...v,
            title: valueParts[0],
          }
        }
        return v
      })
      .filter((v) => {
        if (!v || !v.title || uniqueTitles.includes(v?.title)) {
          return false
        }
        uniqueTitles.push(v.title)
        return true
      })
  }, [dropdownValue])

  const setOptionByColorAndQty = useCallback(
    (colorOptionId, newQuantity) => {
      let qty = newQuantity || Object.keys(quantities).at(0)
      if (qty) {
        const colorTitle = uniqueColorOptions.find(
          (c) => c?.option_type_id?.toString() === colorOptionId,
        )?.title
        const colorOptionFullTitle = `${colorTitle} - ${qty}`
        let selectedRealOption = dropdownValue?.find((v) => v?.title === colorOptionFullTitle)
        if (!selectedRealOption) {
          qty = Object.keys(quantities).at(0)
          const colorOptionFullTitleFirst = `${colorTitle} - ${qty}`
          selectedRealOption = dropdownValue?.find((v) => v?.title === colorOptionFullTitleFirst)
        }
        const selectedOptionId = selectedRealOption?.uid
        if (selectedOptionId) {
          const entered = Array.isArray(enteredOptions)
            ? [...enteredOptions].filter((item) => item)
            : []
          if (entered.find((o) => o.uid === uid)) {
            entered.find((o) => o.uid === uid).value = selectedOptionId
          } else {
            entered.push({ uid, value: selectedOptionId })
          }
          setValue(`enteredOptions`, entered, {})
          setValue(`quantity`, qty)
        }
      }
    },
    [uniqueColorOptions, dropdownValue, enteredOptions, setValue, uid, quantities],
  )

  // DO NOT ADD setOptionByColorAndQty as dependency to avoid loop
  useEffect(() => {
    setOptionByColorAndQty(color, quantity)
  }, [quantity, color])

  useEffect(() => {
    // Autoselect the first color
    if (uniqueColorOptions && uniqueColorOptions.length > 0) {
      setColor(uniqueColorOptions.at(0)?.option_type_id?.toString())
    }
  }, [uniqueColorOptions])

  if (
    !dropdownValue ||
    (fouroverOptionModifier?.modifier === 'DisableIfSingle' && dropdownValue?.length < 2) ||
    fouroverOptionModifier?.modifier === 'Disable'
  ) {
    return null
  }

  const isThreeColTrigger =
    uniqueColorOptions.length > 12 || (title?.toLowerCase().includes('size') ?? false)

  return (
    <div className='my-3.75'>
      <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
        {title}
      </p>
      <div
        className={
          isThreeColTrigger
            ? 'grid grid-cols-1 gap-2 lg:grid-cols-3'
            : 'grid grid-cols-1 gap-2 lg:grid-cols-2'
        }
      >
        {filterNonNullableKeys(uniqueColorOptions, ['title']).map((option) => {
          const isSelected = color === option.option_type_id?.toString()
          return (
            <button
              key={option.option_type_id}
              type='button'
              onClick={() => {
                if (uid && option.option_type_id) {
                  setColor(option.option_type_id.toString())
                }
              }}
              className={`w-full overflow-hidden rounded-[4px] px-2.5 py-2.5 transition-all active:scale-[0.97] ${
                isSelected
                  ? 'bg-[#D1CBCB33] text-black border-[1.6px] border-[#5D54DE] shadow-[0px_2px_3px_0px_rgba(0,0,0,0.25)]'
                  : 'bg-[#F8F8F8] text-black border-[1.4px] border-[#908B8B]'
              }`}
            >
              <span
                className={`block truncate text-[13px] uppercase tracking-[0.04em] ${isSelected ? 'font-bold' : 'font-medium'}`}
              >
                {option.title}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
})
